import React, { useMemo } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Line,
  CartesianGrid,
  ReferenceLine,
  ComposedChart,
} from "recharts";
import { fmtCurrency, fmtCurrencyFull } from "@/lib/format";

const COLORS = {
  primary: "#2B5339",
  accent: "#C26747",
  band: "#728A7C",
};

const Tip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload.reduce((acc, x) => ({ ...acc, [x.dataKey]: x.value }), {});
  return (
    <div className="bg-white border border-[#E5E2DC] rounded-xl px-4 py-3 shadow-sm">
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold mb-1">
        age {label}
      </div>
      {"simple" in p && (
        <div className="text-sm text-[#1C2B23] font-mono-num">
          <span className="text-[#5C6B62] mr-2">simple</span> {fmtCurrencyFull(p.simple)}
        </div>
      )}
      {"p50" in p && (
        <>
          <div className="text-sm text-[#1C2B23] font-mono-num">
            <span className="text-[#5C6B62] mr-2">median</span> {fmtCurrencyFull(p.p50)}
          </div>
          <div className="text-xs text-[#5C6B62] font-mono-num">
            p10 {fmtCurrencyFull(p.p10)} · p90 {fmtCurrencyFull(p.p90)}
          </div>
        </>
      )}
    </div>
  );
};

export default function ProjectionChart({ result, mode, retirementAge }) {
  const data = useMemo(() => {
    if (!result) return [];
    const simpleMap = Object.fromEntries(result.simple.map((d) => [d.age, d.balance]));
    return result.monte_carlo.map((d) => ({
      age: d.age,
      simple: simpleMap[d.age] ?? null,
      p10: d.p10,
      p25: d.p25,
      p50: d.p50,
      p75: d.p75,
      p90: d.p90,
    }));
  }, [result]);

  if (!result) {
    return (
      <div data-testid="projection-empty" className="h-[420px] flex items-center justify-center text-[#5C6B62] text-sm">
        Adjust your plan and click <span className="mx-1 font-medium text-[#1C2B23]">Run projection</span> to see your 100-year outlook.
      </div>
    );
  }

  return (
    <div data-testid="projection-chart" className="w-full h-[420px]">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 20, right: 20, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="band" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={COLORS.band} stopOpacity={0.35} />
              <stop offset="100%" stopColor={COLORS.band} stopOpacity={0.05} />
            </linearGradient>
            <linearGradient id="bandInner" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={COLORS.primary} stopOpacity={0.25} />
              <stop offset="100%" stopColor={COLORS.primary} stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis
            dataKey="age"
            tick={{ fill: "#5C6B62", fontSize: 11 }}
            axisLine={{ stroke: "#E5E2DC" }}
            tickLine={false}
          />
          <YAxis
            tickFormatter={(v) => fmtCurrency(v)}
            tick={{ fill: "#5C6B62", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            width={70}
          />
          <Tooltip content={<Tip />} />
          {retirementAge && (
            <ReferenceLine
              x={retirementAge}
              stroke="#C26747"
              strokeDasharray="4 4"
              label={{ value: "retire", fill: "#C26747", fontSize: 11, position: "top" }}
            />
          )}

          {(mode === "monte" || mode === "both") && (
            <>
              <Area
                type="monotone"
                dataKey="p90"
                stroke="none"
                fill="url(#band)"
                fillOpacity={1}
                isAnimationActive={false}
                activeDot={false}
              />
              <Area
                type="monotone"
                dataKey="p10"
                stroke="none"
                fill="#F9F8F6"
                fillOpacity={1}
                isAnimationActive={false}
                activeDot={false}
              />
              <Area
                type="monotone"
                dataKey="p75"
                stroke="none"
                fill="url(#bandInner)"
                fillOpacity={1}
                isAnimationActive={false}
                activeDot={false}
              />
              <Area
                type="monotone"
                dataKey="p25"
                stroke="none"
                fill="#F9F8F6"
                fillOpacity={1}
                isAnimationActive={false}
                activeDot={false}
              />
              <Line
                type="monotone"
                dataKey="p50"
                stroke={COLORS.primary}
                strokeWidth={2.2}
                dot={false}
                isAnimationActive={true}
              />
            </>
          )}
          {(mode === "simple" || mode === "both") && (
            <Line
              type="monotone"
              dataKey="simple"
              stroke={COLORS.accent}
              strokeWidth={2}
              strokeDasharray={mode === "both" ? "5 4" : "0"}
              dot={false}
              isAnimationActive={true}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
